import * as React from 'react';
import { cn } from '@noor/lib/cn';

export type TopNavShellHeaderProps = Omit<React.ComponentProps<'header'>, 'children'> & {
  /** Brand mark / wordmark pinned to the leading edge. */
  brand?: React.ReactNode;
  /** Primary navigation, centered — typically a `SegmentedNav`. */
  nav?: React.ReactNode;
  /** Utilities pinned to the trailing edge (icon buttons, language, avatar). */
  utilities?: React.ReactNode;
};

/**
 * The top header bar: a full-width surface with the brand at the leading edge,
 * centered primary navigation, and trailing utilities.
 *
 * Responsive: from `lg` up it's a single row with the brand and utilities zones
 * equal-flex so the nav stays optically centered (the Figma "Nav bar" — px-8,
 * py-5, 16px gaps). Below `lg` the primary nav drops to its own full-width row
 * beneath the brand+utilities and scrolls horizontally, so a wide segmented
 * control never overflows or crushes the utilities on small screens.
 */
function TopNavShellHeader({
  className,
  brand,
  nav,
  utilities,
  ...props
}: TopNavShellHeaderProps) {
  return (
    <header
      data-slot="top-nav-shell-header"
      className={cn(
        'flex w-full shrink-0 flex-col gap-3 bg-card px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:gap-4 lg:px-8 lg:py-5',
        className,
      )}
      {...props}
    >
      <div className="flex items-center gap-4 lg:min-w-0 lg:flex-1">
        <div className="flex min-w-0 flex-1 items-center">{brand}</div>
        {/* Utilities ride with the brand row on mobile; move to the trailing
         * edge on desktop. */}
        <div className="flex items-center gap-2 lg:hidden">{utilities}</div>
      </div>

      {nav !== undefined ? (
        <div className="-mx-4 shrink-0 overflow-x-auto px-4 [scrollbar-width:none] sm:-mx-6 sm:px-6 lg:mx-0 lg:overflow-visible lg:px-0 [&::-webkit-scrollbar]:hidden">
          <div className="w-max lg:w-auto">{nav}</div>
        </div>
      ) : null}

      <div className="hidden items-center justify-end gap-2 lg:flex lg:min-w-0 lg:flex-1">
        {utilities}
      </div>
    </header>
  );
}

export { TopNavShellHeader };
